import React, { useState, useEffect } from 'react';
import { authClient } from '../lib/auth';

interface CheckoutModalProps {
  tierId?: string;
  tierName?: string;
  price?: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface Tier {
  id: string;
  name: string;
  storageLimitBytes: number;
  price: number;
  memberLimit: number;
  visibility: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(0)) + ' ' + sizes[i];
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  tierId: initialTierId,
  onClose,
  onSuccess,
}) => {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [selectedTierId, setSelectedTierId] = useState<string>(initialTierId || 'personal');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactNotice, setContactNotice] = useState<string | null>(null);

  const { data: activeOrg } = authClient.useActiveOrganization();

  useEffect(() => {
    fetch('/api/billing/tiers')
      .then((res) => res.json())
      .then((data) => {
        if (data?.tiers) {
          setTiers(data.tiers);
          if (initialTierId && data.tiers.some((t: Tier) => t.id === initialTierId)) {
            setSelectedTierId(initialTierId);
          } else if (data.tiers.length > 0 && !initialTierId) {
            setSelectedTierId(data.tiers[0].id);
          }
        }
      })
      .catch((e) => console.error('Failed to load tiers', e));
  }, [initialTierId]);

  const handleSelectPlan = async (tier: Tier) => {
    if (!activeOrg?.id) {
      setError('Please log in to your organization.');
      return;
    }

    setLoading(true);
    setError(null);
    setContactNotice(null);

    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: activeOrg.id,
          tierId: tier.id,
          cycle: billingCycle,
        }),
      });

      const data = await res.json();

      if (res.status === 501) {
        // Billing not active yet
        setContactNotice(
          `Self-serve billing via Stripe will be available shortly. To upgrade your account to ${tier.name} (${formatBytes(tier.storageLimitBytes)}) right now, please reach out directly.`
        );
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || 'Failed to initiate upgrade');
      }

      if (data.url) {
        window.location.href = data.url;
        return;
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while contacting the billing server.');
    } finally {
      setLoading(false);
    }
  };

  const selectedTier = tiers.find((t) => t.id === selectedTierId);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        className="glass-panel"
        style={{
          padding: '2rem',
          width: '100%',
          maxWidth: '560px',
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          border: '1px solid var(--border-color)',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', margin: 0, color: 'var(--text-primary)' }}>
            Choose Your FileSharez Plan
          </h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem', fontSize: '0.9rem' }}>
            Scale your storage and seats with secure Australian cloud storage.
          </p>
        </div>

        {/* Monthly / Yearly Toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
          <div
            style={{
              display: 'inline-flex',
              background: 'var(--bg-tertiary)',
              borderRadius: '30px',
              padding: '0.25rem',
              border: '1px solid var(--border-color)',
            }}
          >
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              style={{
                padding: '0.4rem 1rem',
                borderRadius: '20px',
                border: 'none',
                background: billingCycle === 'monthly' ? 'var(--bg-primary)' : 'transparent',
                color: billingCycle === 'monthly' ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              style={{
                padding: '0.4rem 1rem',
                borderRadius: '20px',
                border: 'none',
                background: billingCycle === 'yearly' ? 'var(--bg-primary)' : 'transparent',
                color: billingCycle === 'yearly' ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
              }}
            >
              Yearly <span style={{ fontSize: '0.7rem', color: '#22c55e', fontWeight: 'bold' }}>Save 10%</span>
            </button>
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: '0.75rem 1rem',
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {contactNotice && (
          <div
            style={{
              padding: '1rem',
              background: 'rgba(14, 165, 233, 0.1)',
              color: '#38bdf8',
              border: '1px solid rgba(14, 165, 233, 0.3)',
              borderRadius: '8px',
              marginBottom: '1.25rem',
              fontSize: '0.875rem',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>Direct Upgrade Support</div>
            <div>{contactNotice}</div>
            <div style={{ marginTop: '0.75rem' }}>
              <a
                href={`mailto:jp@buzzwebmedia.com.au?subject=Upgrade%20to%20${selectedTier?.name || 'FileSharez'}%20Plan`}
                className="btn btn-primary"
                style={{
                  display: 'inline-block',
                  padding: '0.4rem 0.8rem',
                  fontSize: '0.8rem',
                  textDecoration: 'none',
                }}
              >
                ✉️ Email Admin to Activate Plan
              </a>
            </div>
          </div>
        )}

        {/* Tiers List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {tiers.map((t) => {
            const isSelected = selectedTierId === t.id;
            const monthlyPriceAud = t.price / 100;
            const displayPrice =
              billingCycle === 'yearly'
                ? (monthlyPriceAud * 0.9).toFixed(2)
                : monthlyPriceAud.toFixed(2);

            return (
              <div
                key={t.id}
                onClick={() => setSelectedTierId(t.id)}
                style={{
                  padding: '1rem',
                  borderRadius: '12px',
                  border: isSelected
                    ? '2px solid var(--accent-primary)'
                    : '1px solid var(--border-color)',
                  background: isSelected ? 'rgba(124, 58, 237, 0.05)' : 'var(--bg-tertiary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{t.name}</span>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        background: 'var(--bg-secondary)',
                        padding: '0.15rem 0.4rem',
                        borderRadius: '4px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {formatBytes(t.storageLimitBytes)}
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.2rem' }}>
                    Up to {t.memberLimit} {t.memberLimit === 1 ? 'member' : 'members'}
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    ${displayPrice}
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                      /mo
                    </span>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>+ GST</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            style={{ flex: 1, padding: '0.75rem' }}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading || !selectedTier}
            onClick={() => selectedTier && handleSelectPlan(selectedTier)}
            style={{ flex: 2, padding: '0.75rem' }}
          >
            {loading ? 'Connecting...' : `Upgrade to ${selectedTier?.name || 'Selected Plan'}`}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          Need 5 TB+ or custom requirements? <a href="mailto:jp@buzzwebmedia.com.au?subject=Studio%20Plan%20Inquiry" style={{ color: 'var(--accent-primary)', textDecoration: 'underline' }}>Contact our Studio team</a>.
        </div>
      </div>
    </div>
  );
};
